/**
 * HairTriggerSkillEffect — Conjuration Swarm path terminal passive.
 *
 * Always-on passive. When any wisp is combat-killed (deathCause: 'combat'),
 * it automatically pops for 4 damage in a 1-tile radius at the death
 * position. Detonate kills, expiry, and Convergence kills do NOT trigger.
 *
 * This stacks with Expendable's early-death explosion — if a fresh wisp
 * (within earlyDeathWindowMs) is combat-killed, both fire independently:
 *   - Expendable: 5 damage / 1-tile radius
 *   - Hair Trigger: 4 damage / 1-tile radius
 * Total: 9 damage to an enemy at the death position (two separate hit events).
 *
 * No UpgradeEffectResolver keys — values are fixed at this node.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Hair Trigger
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface HairTriggerDeps {
  readonly combat: {
    /** Apply AoE damage at (x, y) within radiusTiles radius. */
    applyAoEDamage(x: number, y: number, radiusTiles: number, damage: number): void;
  };
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface HairTriggerConfig {
  readonly combatKillExplosionDamage: number;
  readonly combatKillExplosionRadiusTiles: number;
}

const DEFAULT_CONFIG: HairTriggerConfig = {
  combatKillExplosionDamage: 4,      // spec § Hair Trigger
  combatKillExplosionRadiusTiles: 1, // 1-tile radius AoE
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class HairTriggerSkillEffect implements SkillEffect {
  readonly skillId = 'detonate_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: HairTriggerDeps;
  private readonly config: HairTriggerConfig;

  // Observable state for scenarios
  private _explosionCount = 0;
  private _lastExplosionX = 0;
  private _lastExplosionY = 0;

  constructor(deps: HairTriggerDeps, config: Partial<HairTriggerConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Hair Trigger-specific API ---

  /**
   * Handle wisp death. Fires combat-kill explosion if conditions are met.
   *
   * @param _wispId    The dying wisp's ID (not used — Hair Trigger fires for all)
   * @param deathCause 'combat' | 'detonate' | 'expiry' | 'converge'
   * @param x          Death position X (world pixels)
   * @param y          Death position Y (world pixels)
   */
  onWispDied(
    _wispId: string,
    deathCause: 'combat' | 'detonate' | 'expiry' | 'converge',
    x: number,
    y: number,
  ): void {
    if (deathCause !== 'combat') return;

    this.deps.combat.applyAoEDamage(
      x, y,
      this.config.combatKillExplosionRadiusTiles,
      this.config.combatKillExplosionDamage,
    );

    this._explosionCount++;
    this._lastExplosionX = x;
    this._lastExplosionY = y;
  }

  // --- State getters ---

  get explosionCount(): number { return this._explosionCount; }
  get lastExplosionX(): number { return this._lastExplosionX; }
  get lastExplosionY(): number { return this._lastExplosionY; }

  // --- SkillEffect interface ---

  canActivate(): boolean { return false; }
  activate(): boolean { return false; }
  isActive(): boolean { return false; }
  get cooldown(): number { return 0; }
  update(_delta: number): void {}
  destroy(): void {}
}
